// src/public/PublicReasoningTestInstructions.js
import {
    Container, Paper, Typography, Button, List, ListItem,
    ListItemIcon, ListItemText, Box, CircularProgress, Alert
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Timer as TimerIcon,
    Psychology as PsychologyIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getTestTimeLimit } from './testTimeLimits';

const PublicReasoningTestInstructions = ({ testData, onStart, loading }) => {
    const { t } = useTranslation();

    const getInstructionsByType = (testType) => {
        let instructions = [];

        switch (testType) {
            case 'wordAssociation':
                instructions = [
                    t('reasoningTests.wordAssociation16Questions'),
                    // t('reasoningTests.wordAssociation10Minutes'),
                    t('reasoningTests.wordAssociationPairs'),
                    t('reasoningTests.wordAssociationSimilar'),
                    t('reasoningTests.wordAssociationNoPrevious'),
                ];
                break;
            case 'numericalReasoning':
                instructions = [
                    t('reasoningTests.numericalReasoning42Series'),
                    t('reasoningTests.numericalReasoningNext2'),
                    t('reasoningTests.numericalReasoningPractice'),
                ];
                break;
            case 'logicalProcess':
                instructions = [
                    t('reasoningTests.logicalProcess6Problems'),
                    t('reasoningTests.logicalProcessMultipleQuestions'),
                    t('reasoningTests.logicalProcessReadCarefully'),
                ];
                break;
            case 'socialContext':
                instructions = [
                    t('reasoningTests.socialContext20Questions'),
                    // t('reasoningTests.socialContext10Minutes'),
                    t('reasoningTests.socialContextOrderImages'),
                    t('reasoningTests.socialContextDragDrop'),
                    t('reasoningTests.socialContextChronological'),
                ];
                break;
            case 'logicalAbstract':
                instructions = [
                    t('reasoningTests.logicalAbstract30Questions'),
                    // t('reasoningTests.logicalAbstract10Minutes'),
                    t('reasoningTests.logicalAbstractAnalyzeConcepts'),
                    t('reasoningTests.logicalAbstractIdentifyRelation'),
                    t('reasoningTests.logicalAbstractNoPenalty'),
                ];
                break;
            case 'integrityTest':
                instructions = [
                    t('reasoningTests.integrityTest45Questions'),
                    // t('reasoningTests.integrityTest15Minutes'),
                    t('reasoningTests.integrityTestHonestAnswers'),
                    t('reasoningTests.integrityTestConfidential'),
                    t('reasoningTests.integrityTestNoReturn'),
                    t('reasoningTests.integrityTestCanSkip'),
                ];
                break;
            case 'emotionalIntelligence':
                instructions = [
                    t('reasoningTests.emotionalIntelligence48Questions'),
                    // t('reasoningTests.emotionalIntelligence10Minutes'),
                    t('reasoningTests.emotionalIntelligenceLikertScale'),
                    t('reasoningTests.emotionalIntelligenceHonestAnswers'),
                    t('reasoningTests.emotionalIntelligenceNoReturn'),
                ];
                break;
            default:
                instructions = [];
        }

        // Agregar instrucción sobre ejemplos de práctica para TODOS los tests
        if (instructions.length > 0) {
            instructions.push(t('reasoningTests.practiceExamplesInstruction'));
        }

        return instructions;
    };

    const getTestTitle = (testType) => {
        const titles = {
            wordAssociation: t('reasoningTests.wordAssociation'),
            numericalReasoning: t('reasoningTests.numericalReasoning'),
            logicalProcess: t('reasoningTests.logicalProcess'),
            socialContext: t('reasoningTests.socialContext'),
            logicalAbstract: t('reasoningTests.logicalAbstract'),
            integrityTest: t('reasoningTests.integrityTest'),
            emotionalIntelligence: t('reasoningTests.emotionalIntelligence'),
        };
        return titles[testType] || t('reasoningTests.test');
    };

    const getButtonText = (testType) => {
        if (testType === 'numericalReasoning' || testType === 'logicalProcess' || testType === 'wordAssociation' || testType === 'logicalAbstract' || testType === 'integrityTest' || testType === 'emotionalIntelligence' || testType === 'socialContext') {
            return t('reasoningTests.goToExamples');
        }
        return t('reasoningTests.startTest');
    };

    const instructions = getInstructionsByType(testData?.testType);

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <PsychologyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        {getTestTitle(testData?.testType)}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('reasoningTests.instructions')}
                    </Typography>
                </Box>

                <List sx={{ mb: 4 }}>
                    {instructions.map((instruction, index) => (
                        <ListItem key={index}>
                            <ListItemIcon>
                                <CheckCircleIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={instruction} />
                        </ListItem>
                    ))}
                </List>

                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        {t('reasoningTests.activityMonitoringDisclaimer')}
                    </Typography>
                </Alert>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1" color="text.secondary">
                        {t('reasoningTests.timeRemaining')}: {getTestTimeLimit(testData?.testType)}:00
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={loading ? null : <PsychologyIcon />}
                        onClick={onStart}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : getButtonText(testData?.testType)}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default PublicReasoningTestInstructions;